<template>
  <div class="login-view">
    <!-- Welcome header -->
    <div class="auth-welcome">
      <h1 class="welcome-title">
        {{ $t('auth.welcome-back') }}
      </h1>
      <p class="welcome-subtitle">
        {{ $t('landing.hero-title') }}
      </p>
    </div>

    <!-- Two-Factor Auth Form -->
    <div
      v-if="showTwoFactorForm"
      class="two-factor-section"
    >
      <div class="form-divider mb-4">
        <div class="divider-line" />
        <span class="divider-text">{{ $t('auth.verification') }}</span>
        <div class="divider-line" />
      </div>

      <p class="subtitle has-text-centered is-6 mb-4">
        {{ $t('auth.two-factor-prompt') }}
      </p>

      <form @submit.prevent="submitTwoFactorCode">
        <div class="indiefy-field">
          <BaseInput
            v-model="twoFactorCode"
            :label="$t('auth.two-factor-code')"
            :placeholder="$t('auth.enter-two-factor-code')"
            type="text"
            data-test="2fa-code-input"
            :has-error="!!formErrors.twoFactorCode"
            :error-message="formErrors.twoFactorCode"
            left-icon="fas fa-shield-alt"
            has-floating-label
            required
            autocomplete="one-time-code"
            @blur="validateTwoFactorCode"
          />
          <div class="code-input-hint mt-2">
            <span class="has-text-grey is-size-7">
              <i class="fas fa-info-circle mr-1" />
              {{ $t('auth.check-email-or-phone') }}
            </span>
          </div>
        </div>

        <div class="field mt-5 has-text-centered">
          <BaseButton
            type="submit"
            variant="primary"
            is-fullwidth
            :is-loading="isLoading"
            data-test="submit-2fa-button"
            icon="fas fa-shield-alt"
            is-animated
          >
            {{ $t('auth.verify') }}
          </BaseButton>
        </div>

        <div class="has-text-centered mt-4">
          <a
            class="back-link"
            @click="resetLogin"
          >
            <i class="fas fa-arrow-left mr-1" />
            {{ $t('general.back') }}
          </a>
        </div>
      </form>
    </div>

    <!-- Main Login Form -->
    <div
      v-else
      class="login-form-section"
    >
      <form @submit.prevent="submitLogin">
        <div class="indiefy-field">
          <BaseInput
            v-model="username"
            :label="$t('auth.email')"
            :placeholder="$t('auth.email')"
            type="email"
            data-test="email-input"
            :has-error="!!formErrors.username"
            :error-message="formErrors.username"
            left-icon="fas fa-envelope"
            has-floating-label
            required
            autocomplete="username"
            @blur="validateUsername"
          />
        </div>

        <div class="indiefy-field">
          <BaseInput
            v-model="password"
            :label="$t('auth.password')"
            :placeholder="$t('auth.password')"
            type="password"
            data-test="password-input"
            :has-error="!!formErrors.password"
            :error-message="formErrors.password"
            left-icon="fas fa-lock"
            has-floating-label
            required
            autocomplete="current-password"
            @blur="validatePassword"
          />
        </div>

        <!-- Captcha (placed behind/below the password field) -->
        <div
          v-if="captchaImageUrl"
          class="indiefy-field mt-4"
        >
          <div class="captcha-container">
            <div class="captcha-wrapper p-3 mb-3 is-flex is-align-items-center is-justify-content-space-between">
              <div v-html="captchaImageUrl" />
              <button
                type="button"
                class="refresh-captcha-btn"
                title="Refresh captcha"
                @click="refreshCaptcha"
              >
                <i class="fas fa-sync-alt" />
              </button>
            </div>
          </div>
          <BaseInput
            v-model="captchaValue"
            :label="$t('auth.captcha')"
            :placeholder="$t('auth.enter-captcha')"
            type="text"
            data-test="captcha-input"
            :has-error="!!formErrors.captcha"
            :error-message="formErrors.captcha"
            left-icon="fas fa-robot"
            required
            @blur="validateCaptcha"
          />
        </div>

        <div class="field mt-4 options-row">
          <div class="is-flex is-justify-content-space-between is-align-items-center">
            <label class="checkbox remember-me is-flex is-align-items-center">
              <div class="custom-checkbox">
                <input
                  v-model="rememberMe"
                  type="checkbox"
                  class="mr-2"
                >
                <div class="checkbox-indicator">
                  <i class="fas fa-check" />
                </div>
              </div>
              <span>
                {{ $t('auth.remember-me') }}
              </span>
            </label>

            <router-link
              :to="{ name: 'forgot-password' }"
              class="forgot-password-link"
            >
              {{ $t('auth.forgot-password') }}
            </router-link>
          </div>
        </div>

        <AlertNotification
          v-if="loginError"
          type="warning"
          :inline="true"
          :message="$t(`login.${loginError}`)"
        />

        <div class="mt-5 has-text-centered">
          <BaseButton
            type="submit"
            variant="primary"
            is-fullwidth
            :is-loading="isLoading"
            data-test="login-button"
            is-gradient
            is-3d
          >
            {{ $t('auth.sign-in') }}
          </BaseButton>
        </div>

        <div
          v-if="authError"
          class="error-container mt-4 p-3"
        >
          <i class="fas fa-exclamation-circle mr-2" />
          {{ authError }}
        </div>
      </form>

      <div class="sign-up-prompt mt-5">
        <div class="form-divider mb-4">
          <div class="divider-line" />
          <span class="divider-text">{{ $t('auth.or-continue-with') }}</span>
          <div class="divider-line" />
        </div>

        <div class="social-login-buttons mb-4 is-flex">
          <button
            class="social-button google"
            :disabled="isLoading"
            @click="loginWithGoogle"
          >
            <i class="fab fa-google" />
          </button>
          <button
            class="social-button apple"
            :disabled="isLoading"
            @click="loginWithApple"
          >
            <i class="fab fa-apple" />
          </button>
          <!-- <button class="social-button facebook">
            <i class="fab fa-facebook-f" />
          </button> -->
          <!-- <button class="social-button twitter">
            <i class="fab fa-twitter" />
          </button> -->
        </div>

        <div class="has-text-centered">
          <p class="mb-2">
            {{ $t('auth.no-account') }}
          </p>
          <router-link
            :to="{ name: 'signup' }"
            class="sign-up-button"
          >
            {{ $t('auth.sign-up') }} <i class="fas fa-arrow-right ml-1" />
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import BaseInput from '@/components/ui/BaseInput.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import { useI18n } from 'vue-i18n';
import { usePageSeo } from '@/composables/usePageSeo';

usePageSeo({
  titleKey: 'auth.login-title',
  descriptionKey: 'auth.login-description',
});

// Router and route
const router = useRouter();
const route = useRoute();

// Auth store
const authStore = useAuthStore();
const isLoading = computed(() => authStore.isLoading);
const authError = computed(() => authStore.authError);

// i18n
const { t } = useI18n();

// Form state
const loginError = ref('');
const username = ref('');
const password = ref('');
const captchaValue = ref('');
const rememberMe = ref(false);
const twoFactorCode = ref('');
const showTwoFactorForm = ref(false);
const formErrors = ref({
  username: '',
  password: '',
  captcha: '',
  twoFactorCode: '',
});

// Captcha
const captchaImageUrl = ref('');

// Form validation
const validateUsername = () => {
  if (!username.value) {
    formErrors.value.username = t('errors.required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username.value)) {
    formErrors.value.username = t('errors.email-invalid');
  } else {
    formErrors.value.username = '';
  }
};

const validatePassword = () => {
  if (!password.value) {
    formErrors.value.password = t('errors.required');
  } else {
    formErrors.value.password = '';
  }
};

const validateCaptcha = () => {
  if (!captchaValue.value) {
    formErrors.value.captcha = t('errors.required');
  } else {
    formErrors.value.captcha = '';
  }
};

const validateTwoFactorCode = () => {
  if (!twoFactorCode.value) {
    formErrors.value.twoFactorCode = t('errors.required');
  } else {
    formErrors.value.twoFactorCode = '';
  }
};

const validateForm = () => {
  validateUsername();
  validatePassword();
  if (captchaImageUrl.value) validateCaptcha();

  return !formErrors.value.username &&
         !formErrors.value.password &&
         (!captchaImageUrl.value || !formErrors.value.captcha);
};

const validateTwoFactorForm = () => {
  validateTwoFactorCode();
  return !formErrors.value.twoFactorCode;
};

// Load captcha from server
const loadCaptcha = async () => {
  try {
    const captchaData = await authStore.getCaptcha();
    if (captchaData) {
      captchaImageUrl.value = captchaData;
    }
  } catch (error) {
    console.error('Failed to load captcha:', error);
    // Fallback to placeholder in case of error
    captchaImageUrl.value = 'https://via.placeholder.com/200x60/f4f4f4/333333?text=CAPTCHA';
  }
};

const refreshCaptcha = () => {
  captchaValue.value = '';
  loadCaptcha();
};

// Form submission
const submitLogin = async () => {
  if (!validateForm()) return;

  loginError.value = '';
  authStore.clearError();

  // Include captcha value from the form with default locale
  const result = await authStore.login(
    username.value,
    password.value,
    captchaValue.value,
  );

  if (result.requiresTwoFactor) {
    showTwoFactorForm.value = true;
  } else if (result.blocked) {
    // User is blocked, redirect to blocked page
    router.push('/account/blocked');
  } else if (result.success) {
    handleRedirect();
  } else {
    loginError.value = result.error;

    // Refresh captcha on failure
    refreshCaptcha();
  }
};

const submitTwoFactorCode = async () => {
  if (!validateTwoFactorForm()) return;

  authStore.clearError();

  const result = await authStore.confirmTwoFactor(twoFactorCode.value);

  if (result.blocked) {
    // User is blocked, redirect to blocked page
    router.push('/account/blocked');
  } else if (result.success) {
    handleRedirect();
  }
};

const resetLogin = () => {
  showTwoFactorForm.value = false;
  twoFactorCode.value = '';
  formErrors.value.twoFactorCode = '';
  authStore.clearError();
};

// Handle redirect after successful login
const handleRedirect = () => {
  const redirectPath = route.query.redirect || '/dashboard';

  // If redirect starts with https://, use location.href for external redirect
  if (typeof redirectPath === 'string' && redirectPath.startsWith('https://')) {
    window.location.href = redirectPath;
  } else {
    router.push(redirectPath);
  }
};

// Google login
const loginWithGoogle = async () => {
  authStore.clearError();

  try {
    const result = await authStore.loginWithGoogle();

    if (result.requiresTwoFactor) {
      showTwoFactorForm.value = true;
    } else if (result.blocked) {
      // User is blocked, redirect to blocked page
      router.push('/account/blocked');
    } else if (result.success) {
      handleRedirect();
    }
  } catch (error) {
    console.error('Google login error:', error);
    // Error is already set in the store
  }
};

// Apple login
const loginWithApple = async () => {
  authStore.clearError();

  try {
    const result = await authStore.loginWithApple();

    if (result.requiresTwoFactor) {
      showTwoFactorForm.value = true;
    } else if (result.blocked) {
      // User is blocked, redirect to blocked page
      router.push('/account/blocked');
    } else if (result.success) {
      handleRedirect();
    }
  } catch (error) {
    console.error('Apple login error:', error);
    // Error is already set in the store
  }
};

// Lifecycle hooks
onMounted(() => {
  window.smartlook && window.smartlook('track', 'Login Page');
  loadCaptcha();
});
</script>

<style lang="scss" scoped>
@import '@/styles/abstracts/colors';
@import '@/styles/abstracts/breakpoints';

.login-view {
  .auth-welcome {
    text-align: center;
    margin-bottom: 2rem;

    .welcome-title {
      font-size: 1.75rem;
      font-weight: 700;
      background: linear-gradient(90deg, var(--color-primary, #{$indiefy-blue}), var(--color-secondary, #{$indiefy-lavender}));
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      margin-bottom: 0.5rem;

      @media screen and (min-width: $tablet) {
        font-size: 2rem;
      }
    }

    .welcome-subtitle {
      color: var(--color-text-light, #64748b);
      font-size: 1rem;

      .body--dark & {
        color: var(--color-text-light, #94a3b8);
      }
    }
  }
  .title {
    font-size: 1.75rem;
    font-weight: 700;
    background: linear-gradient(90deg, var(--color-primary, #{$indiefy-blue}), var(--color-secondary, #{$indiefy-lavender}));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 0.5rem;

    @media screen and (min-width: $tablet) {
      font-size: 2rem;
    }
  }

  .subtitle {
    color: var(--color-text-light, #64748b); /* Fallback color */
    font-weight: 400;

    .body--dark & {
      color: var(--color-text-light, #94a3b8); /* Fallback color */
    }
  }

  // Intro card styling
  .intro-card {
    background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
    border-radius: 12px;
    padding: 1.5rem;
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(255,255,255,0.1);

    .body--dark & {
      background: linear-gradient(135deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.1) 100%);
      border: 1px solid rgba(255,255,255,0.05);
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(circle at 50% 50%, rgba(var(--color-primary-rgb, 62, 142, 208), 0.1) 0%, rgba(var(--color-primary-rgb, 62, 142, 208), 0) 70%);
      z-index: 0;
    }
  }

  // Pulse animation for welcome intro
  .pulse-container {
    position: relative;
    width: 60px;
    height: 60px;
    margin: 0 auto 1.5rem;

    .pulse-ring {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 50%;
      border: 3px solid var(--color-primary, #{$indiefy-blue});
      opacity: 0;
      animation: pulse-ring 2s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite;
    }

    .pulse-circle {
      position: relative;
      width: 60px;
      height: 60px;
      background: linear-gradient(135deg, var(--color-primary, #{$indiefy-blue}), var(--color-secondary, #{$indiefy-lavender}));
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 1.5rem;
      box-shadow: 0 5px 15px rgba(var(--color-primary-rgb, 62, 142, 208), 0.3);

      i {
        animation: pulse-icon 2s ease infinite;
      }
    }
  }

  // Form divider
  .form-divider {
    display: flex;
    align-items: center;
    margin: 1.5rem 0;

    .divider-line {
      flex: 1;
      height: 1px;
      background-color: rgba(0, 0, 0, 0.1);

      .body--dark & {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }

    .divider-text {
      padding: 0 1rem;
      font-size: 0.875rem;
      color: var(--color-text-light, #64748b);

      .body--dark & {
        color: var(--color-text-light, #94a3b8);
      }
    }
  }

  // Captcha styling
  .captcha-container {
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: repeating-linear-gradient(
        45deg,
        transparent,
        transparent 10px,
        rgba(0, 0, 0, 0.02) 10px,
        rgba(0, 0, 0, 0.02) 20px
      );
      border-radius: 8px;
      z-index: 0;

      .body--dark & {
        background: repeating-linear-gradient(
          45deg,
          transparent,
          transparent 10px,
          rgba(255, 255, 255, 0.02) 10px,
          rgba(255, 255, 255, 0.02) 20px
        );
      }
    }
  }

  .captcha-wrapper {
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    background-color: var(--color-card-background, #ffffff);
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
    position: relative;
    z-index: 1;

    .body--dark & {
      background-color: white;
      border-color: rgba(255, 255, 255, 0.05);
    }

    .refresh-captcha-btn {
      background-color: var(--color-primary, #{$indiefy-blue});
      color: white;
      border: none;
      cursor: pointer;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
      box-shadow: 0 2px 5px rgba(var(--color-primary-rgb, 62, 142, 208), 0.3);

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(circle at center, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%);
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      &:hover {
        transform: rotate(15deg);

        &::before {
          opacity: 1;
        }
      }

      &:active {
        transform: scale(0.95) rotate(180deg);
      }
    }
  }

  // Custom checkbox styling
  .remember-me {
    position: relative;
    cursor: pointer;
    user-select: none;

    .custom-checkbox {
      position: relative;
      width: 20px;
      height: 20px;
      margin-right: 8px;

      input[type="checkbox"] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      .checkbox-indicator {
        position: absolute;
        top: 0;
        left: 0;
        width: 20px;
        height: 20px;
        background-color: var(--color-card-background, #ffffff);
        border: 1px solid rgba(0, 0, 0, 0.2);
        border-radius: 4px;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;

        .body--dark & {
          background-color: rgba(0, 0, 0, 0.2);
          border-color: rgba(255, 255, 255, 0.1);
        }

        i {
          color: white;
          font-size: 0.75rem;
          opacity: 0;
          transform: scale(0);
          transition: all 0.2s ease;
        }
      }

      input[type="checkbox"]:checked ~ .checkbox-indicator {
        background-color: var(--color-primary, #{$indiefy-blue});
        border-color: var(--color-primary, #{$indiefy-blue});

        i {
          opacity: 1;
          transform: scale(1);
        }
      }

      input[type="checkbox"]:focus ~ .checkbox-indicator {
        box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb, 62, 142, 208), 0.25);
      }
    }
  }

  // Forgot password link
  .forgot-password-link {
    color: var(--color-primary, #{$indiefy-blue});
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 500;
    padding: 0.25rem;
    border-radius: 4px;
    transition: all 0.2s ease;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 1px;
      background-color: var(--color-primary, #{$indiefy-blue});
      transform: scaleX(0);
      transform-origin: right;
      transition: transform 0.3s ease;
    }

    &:hover {
      color: var(--color-primary-dark, #2c6592);

      &::after {
        transform: scaleX(1);
        transform-origin: left;
      }
    }
  }

  // Social login buttons
  .social-login-buttons {
    justify-content: center;
    gap: 0.5rem;

    .social-button {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      border: none;
      cursor: pointer;
      transition: all 0.2s ease;
      margin: 0 0.25rem;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
      }

      &:active {
        transform: translateY(0);
      }

      &.google {
        background-color: #DB4437;
      }

      &.apple {
        background-color: #000000;
      }

      &.facebook {
        background-color: #4267B2;
      }

      &.twitter {
        background-color: #1DA1F2;
      }
    }
  }

  // Sign up button
  .sign-up-button {
    background: linear-gradient(90deg, var(--color-primary, #{$indiefy-blue}), var(--color-secondary, #{$indiefy-lavender}));
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 600;
    display: inline-block;
    padding: 0.5rem 1rem;
    border-radius: 50px;
    position: relative;
    transition: all 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, rgba(var(--color-primary-rgb, 62, 142, 208), 0.1), rgba(var(--color-secondary-rgb, 124, 58, 237), 0.1));
      border-radius: 50px;
      z-index: -1;
      transition: all 0.3s ease;
    }

    &:hover {
      transform: translateX(5px);

      &::before {
        transform: scale(1.05);
      }

      i {
        transform: translateX(3px);
      }
    }

    i {
      transition: transform 0.3s ease;
    }
  }

  // Back link for 2FA form
  .back-link {
    color: var(--color-text-light, #64748b);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    transition: all 0.2s ease;
    padding: 0.5rem;
    border-radius: 4px;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
      color: var(--color-text, #0f172a);

      .body--dark & {
        background-color: rgba(255, 255, 255, 0.05);
        color: var(--color-text, #f8fafc);
      }

      i {
        transform: translateX(-3px);
      }
    }

    i {
      transition: transform 0.2s ease;
    }
  }

  // 2FA code hint
  .code-input-hint {
    animation: fadeIn 0.5s ease;
  }

  // Error container
  .error-container {
    background-color: rgba(239, 68, 68, 0.1);
    border-left: 4px solid #ef4444;
    border-radius: 4px;
    color: #ef4444;
    animation: shake 0.5s ease;
  }
}

// Additional keyframes
@keyframes pulse-ring {
  0% {
    transform: scale(0.8);
    opacity: 0.8;
  }
  80%, 100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

@keyframes pulse-icon {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-5px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(5px);
  }
}
</style>